import React from 'react';
import { Icon } from '@shopify/polaris';

/**
 * Segmented tab switch with an icon (or image mark) per tab and an optional
 * count pill. Polaris <Tabs> only accepts string content, which is why this
 * exists; it is drawn on Polaris tokens (#f1f1f1 track, #303030 / #616161
 * text, #cdfee1 / #0c5132 count on the active tab) so it sits with the rest
 * of the admin.
 *
 * Shared by Staff and Reports - one switch, two pages, no drift.
 *
 * tabs: [{ id, label, icon?, image?, count? }]
 */
export default function SegmentedTabs({ tabs, selected, onSelect }) {
  return (
    <div role="tablist" className="inline-flex rounded-[10px] p-1" style={{ backgroundColor: '#f1f1f1' }}>
      {tabs.map((tab, i) => {
        const active = i === selected;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(i)}
            className="inline-flex items-center gap-2 rounded-[8px] border-0 px-3.5 py-2 text-[13px] font-semibold transition-colors"
            style={{
              backgroundColor: active ? '#ffffff' : 'transparent',
              color: active ? '#303030' : '#616161',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {(tab.image || tab.icon) && (
              /* Sized wrapper: Polaris .Polaris-Icon carries margin:auto and would
                 otherwise swallow the row's free space as a bare flex child. */
              <span className="inline-flex h-4 w-4 flex-none items-center justify-center">
                {tab.image
                  ? <img src={tab.image} alt="" className="h-4 w-auto" />
                  : <Icon source={tab.icon} tone={active ? 'base' : 'subdued'} />}
              </span>
            )}
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span
                className="rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none"
                style={{ backgroundColor: active ? '#cdfee1' : '#e3e3e3', color: active ? '#0c5132' : '#616161' }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
